import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "@/lib/client";
import { Card, CardContent } from "@shared-frontend/components/ui/card";
import { AlertCircle, Loader2 } from "lucide-react";
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from "@shared-frontend/components/ui/alert";

/**
 * Palantir OAuth callback page - handles the redirect after Palantir/Foundry login
 *
 * This is separate from AuthCallbackPage (platform Auth0) to avoid OAuth state conflicts.
 */
function PalantirAuthCallbackPage() {
    const [error, setError] = useState<string | undefined>(undefined);
    const navigate = useNavigate();

    useEffect(() => {
        const completeAuth = async () => {
            try {
                await auth.signIn();
                // Check if there was a saved redirect location
                const savedLocation = sessionStorage.getItem("auth_redirect");
                sessionStorage.removeItem("auth_redirect");

                // Only allow relative paths to prevent open redirect attacks.
                // Reject absolute URLs, protocol-relative URLs, and data/javascript URIs.
                const isRelativePath =
                    savedLocation &&
                    savedLocation.startsWith("/") &&
                    !savedLocation.startsWith("//");

                navigate(isRelativePath ? savedLocation : "/", { replace: true });
            } catch (e: unknown) {
                setError((e as Error).message ?? (e as any));
            }
        };

        completeAuth();
    }, [navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <Card className="max-w-md w-full">
                <CardContent className="p-6">
                    {error != null ? (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Palantir Authentication Error</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ) : (
                        <div className="text-center">
                            <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
                            <p className="mt-4 text-muted-foreground">
                                Completing Palantir authentication...
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export default PalantirAuthCallbackPage;
